// PersonalNarrationContext.
//
// Owns the background-job lifecycle for the personal narrator
// (F5-TTS clone tier). Lives BELOW the router so an active job
// keeps polling while the writer navigates away from the reader,
// fixes the "I clicked play, switched tabs, came back, nothing
// happened" pain. Persists the active inferenceId to
// localStorage so a hard refresh resumes polling instead of
// orphaning the SageMaker job.
//
// Exposes:
//   * ``activeJob`` — { status, elapsedSec, requestKey, ... }
//   * ``submit({...})`` — kicks a new job, replaces any in-flight one
//   * ``dismiss()`` — clears state + storage (writer acknowledged
//                     a ready / failed job)
//   * ``replay()`` — re-fetches the audio URL when status='ready'
//                    (presigned URLs expire in 10 min; re-fetch
//                    keeps cached playback working past that)
//
// Status state machine:
//   idle → submitting → pending → ready
//                           ↘ failed
//                       ↘ failed (submit network failure)
//
// Components subscribe via ``usePersonalNarrationJob()``. The
// reader's personal tab dispatches ``submit()`` instead of
// calling synthesizePersonal directly; the persistent toast at
// app root reads ``activeJob`` to fire react-toastify on done.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  PERSONAL_POLL_BACKOFFS,
  PERSONAL_POLL_MAX_SECONDS,
  pollPersonalSynthOnce,
  submitPersonalSynth,
} from '../util/ttsApi';
import { QuotaExceededError } from '../util/quotaErrors';
import { logger } from '../util/logger';

const STORAGE_KEY = 'writeon:tts:personal-job';

// Match the existing ``writeon:*`` namespace convention. Only the
// minimum needed to resume polling on refresh -- audio URL is
// presigned so we don't persist it (re-fetch on demand).
function loadStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.inferenceId === 'string' &&
      typeof parsed.startedAt === 'number'
    ) {
      // Stale-resume guard. If the persisted job is older than
      // the poll budget, the SageMaker async output has expired
      // (24 h S3 lifecycle on async-out) -- don't try to resume.
      const ageSec = (Date.now() - parsed.startedAt) / 1000;
      if (ageSec > PERSONAL_POLL_MAX_SECONDS + 60) return null;
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function persist(snapshot) {
  try {
    if (snapshot == null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      // Persist only the resume-essential fields so a refresh can
      // reattach. Skip transient (elapsedSec, status) -- they're
      // recomputed on hydrate.
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          inferenceId: snapshot.inferenceId,
          startedAt: snapshot.startedAt,
          requestKey: snapshot.requestKey,
          cached: snapshot.cached,
          chapterTitle: snapshot.chapterTitle,
          firstWait: snapshot.firstWait,
        }),
      );
    }
  } catch {
    /* ignore - storage may be blocked */
  }
}

const IDLE_JOB = {
  status: 'idle',
  inferenceId: null,
  startedAt: null,
  elapsedSec: 0,
  audioUrl: null,
  cached: false,
  error: null,
  requestKey: null,
  chapterTitle: null,
};

const PersonalNarrationContext = createContext({
  activeJob: IDLE_JOB,
  submit: () => Promise.resolve(),
  dismiss: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export function usePersonalNarrationJob() {
  return useContext(PersonalNarrationContext);
}

export function PersonalNarrationProvider({ children }) {
  const [job, setJob] = useState(() => {
    const stored = loadStored();
    if (!stored) return IDLE_JOB;
    return {
      ...IDLE_JOB,
      status: 'pending',
      inferenceId: stored.inferenceId,
      startedAt: stored.startedAt,
      elapsedSec: Math.round((Date.now() - stored.startedAt) / 1000),
      cached: !!stored.cached,
      requestKey: stored.requestKey,
      chapterTitle: stored.chapterTitle || null,
    };
  });

  // Keep the latest job in a ref so the polling effect can read
  // it without re-subscribing on every state change. Without this
  // the setInterval would close over a stale job snapshot.
  const jobRef = useRef(job);
  jobRef.current = job;

  // Track outstanding poll so a fast-fire submit() that replaces
  // a pending job stops the prior poll cleanly.
  const pollAttemptRef = useRef(0);

  // ------------------------------------------------------------
  // Polling loop. Runs whenever status === 'pending'. Survives
  // navigation because the provider lives above the router.
  // ------------------------------------------------------------
  useEffect(() => {
    if (job.status !== 'pending' || !job.inferenceId) return undefined;

    let cancelled = false;
    pollAttemptRef.current = 0;
    const startMs = job.startedAt || Date.now();

    const tick = async () => {
      if (cancelled) return;
      const attempt = pollAttemptRef.current;
      pollAttemptRef.current += 1;
      const elapsed = Math.round((Date.now() - startMs) / 1000);
      // Surface elapsed seconds on every tick so the UI can show
      // "Generating… (3:42)".
      setJob((prev) => {
        if (prev.status !== 'pending') return prev;
        return { ...prev, elapsedSec: elapsed };
      });

      if (elapsed > PERSONAL_POLL_MAX_SECONDS) {
        if (cancelled) return;
        logger.warn('personal_narration_timed_out', {
          inferenceId: job.inferenceId,
          elapsedSec: elapsed,
        });
        setJob({
          ...IDLE_JOB,
          status: 'failed',
          inferenceId: job.inferenceId,
          startedAt: job.startedAt,
          elapsedSec: elapsed,
          error: 'timeout',
          requestKey: job.requestKey,
          chapterTitle: job.chapterTitle,
        });
        persist(null);
        return;
      }

      let resp;
      try {
        resp = await pollPersonalSynthOnce(job.inferenceId);
      } catch (err) {
        if (cancelled) return;
        // Transient poll failure -- back off + retry. Don't fail
        // the whole job on a single network blip.
        logger.warn('personal_narration_poll_blip', {
          inferenceId: job.inferenceId,
          message: err?.message,
        });
        const waitSec = pickBackoff(attempt + 1);
        setTimeout(tick, waitSec * 1000);
        return;
      }
      if (cancelled) return;

      if (resp?.status === 'ready' && resp.audio_url) {
        setJob({
          ...IDLE_JOB,
          status: 'ready',
          inferenceId: job.inferenceId,
          startedAt: job.startedAt,
          elapsedSec: Math.round((Date.now() - startMs) / 1000),
          audioUrl: resp.audio_url,
          cached: job.cached,
          requestKey: job.requestKey,
          chapterTitle: job.chapterTitle,
        });
        persist(null);
        return;
      }
      if (resp?.status === 'failed') {
        setJob({
          ...IDLE_JOB,
          status: 'failed',
          inferenceId: job.inferenceId,
          startedAt: job.startedAt,
          elapsedSec: Math.round((Date.now() - startMs) / 1000),
          error: 'sagemaker_failed',
          requestKey: job.requestKey,
          chapterTitle: job.chapterTitle,
        });
        persist(null);
        return;
      }
      // pending → schedule next tick with backoff.
      const waitSec = pickBackoff(attempt + 1);
      setTimeout(tick, waitSec * 1000);
    };

    // Kick the first poll after the BE-recommended firstWait
    // (passed in via submit() / stored on resume) so we don't
    // hammer S3 immediately. Cached jobs come back with
    // firstWait=0 since the result is already in the cache.
    const initialWait =
      job.cached ? 0 : (job.firstWait != null ? job.firstWait : PERSONAL_POLL_BACKOFFS[0]);
    const initial = setTimeout(tick, initialWait * 1000);

    return () => {
      cancelled = true;
      clearTimeout(initial);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.status, job.inferenceId]);

  // Last submitted params -- kept in a ref so a retry() can
  // replay the exact same submit without the caller having to
  // remember the chapter text / sample / lang. Cleared on
  // dismiss() so a stale retry doesn't accidentally fire after
  // the writer moved on.
  const lastSubmitRef = useRef(null);

  const submit = useCallback(async (params) => {
    // Each submit() replaces any prior active job. The
    // dependency-array trick above means the polling effect for
    // the old inferenceId will see cancelled=true and bail.
    const requestKey = params.requestKey || `${Date.now()}`;
    lastSubmitRef.current = params;
    setJob({
      ...IDLE_JOB,
      status: 'submitting',
      requestKey,
      chapterTitle: params.chapterTitle || null,
      startedAt: Date.now(),
    });
    let submitResult;
    try {
      submitResult = await submitPersonalSynth({
        text: params.text,
        voiceSampleS3Key: params.voiceSampleS3Key,
        voiceReferenceText: params.voiceReferenceText,
        language: params.language || 'en',
      });
    } catch (err) {
      if (err instanceof QuotaExceededError) {
        setJob({
          ...IDLE_JOB,
          status: 'failed',
          error: 'quota',
          requestKey,
          chapterTitle: params.chapterTitle || null,
          startedAt: Date.now(),
        });
        throw err;
      }
      logger.warn('personal_narration_submit_failed', { message: err?.message });
      setJob({
        ...IDLE_JOB,
        status: 'failed',
        error: err?.message || 'submit_failed',
        requestKey,
        chapterTitle: params.chapterTitle || null,
        startedAt: Date.now(),
      });
      throw err;
    }
    const startedAt = Date.now();
    const next = {
      ...IDLE_JOB,
      status: 'pending',
      inferenceId: submitResult.inferenceId,
      startedAt,
      elapsedSec: 0,
      cached: submitResult.cached,
      requestKey,
      chapterTitle: params.chapterTitle || null,
      firstWait: submitResult.firstWait,
    };
    setJob(next);
    persist(next);
    return submitResult;
  }, []);

  const dismiss = useCallback(() => {
    setJob(IDLE_JOB);
    persist(null);
    lastSubmitRef.current = null;
  }, []);

  // Replay the previous submit -- used by the "Retry" button on
  // a failed job banner so the writer doesn't have to re-type
  // anything. Quota: BE submitPersonalSynth re-charges every
  // call; the retry behaviour is documented in the failed-banner
  // copy so the writer knows.
  const retry = useCallback(async () => {
    const params = lastSubmitRef.current;
    if (!params) return null;
    return submit({ ...params, requestKey: `retry-${Date.now()}` });
  }, [submit]);

  // Refresh the presigned audio URL without re-running the synth.
  // The BE returns a 1-hour presigned URL on each /tts/job/{id}
  // poll; if the writer gets the "ready" toast but doesn't click
  // play for >1 hour, the URL expires and the audio element
  // silently fails (currentTime stays at 0:00, no error UI). This
  // re-polls the job endpoint, gets a fresh URL pointing at the
  // same cached blob (no SageMaker cost), and writes it back to
  // activeJob.audioUrl so the player can re-attach.
  //
  // Returns the fresh URL on success, ``null`` if the job state
  // shifted (no longer ready) or the poll itself failed.
  const refreshUrl = useCallback(async () => {
    const cur = jobRef.current;
    if (cur.status !== 'ready' || !cur.inferenceId) return null;
    try {
      const resp = await pollPersonalSynthOnce(cur.inferenceId);
      if (resp?.status === 'ready' && resp.audio_url) {
        setJob((prev) => (
          prev.status === 'ready' && prev.inferenceId === cur.inferenceId
            ? { ...prev, audioUrl: resp.audio_url }
            : prev
        ));
        return resp.audio_url;
      }
      return null;
    } catch (err) {
      logger.warn('personal_narration_refresh_url_failed', {
        inferenceId: cur.inferenceId,
        message: err?.message,
      });
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({ activeJob: job, submit, dismiss, retry, refreshUrl, canRetry: !!lastSubmitRef.current }),
    [job, submit, dismiss, retry, refreshUrl],
  );

  return (
    <PersonalNarrationContext.Provider value={value}>
      {children}
    </PersonalNarrationContext.Provider>
  );
}

function pickBackoff(attempt) {
  const idx = Math.min(attempt, PERSONAL_POLL_BACKOFFS.length - 1);
  return PERSONAL_POLL_BACKOFFS[idx];
}
